// client/src/components/common/LexiStarNotificationBell.tsx (converted from .jsx, ST-00251)
//
// LexiStar in-app notification bell (ST-00188 M3, T8-S02; LS-09 §3 app shell
// "notification bell with unread count"; m3-contracts §7.2 in-app centre).
//
//  - Unread count from GET /api/lexistar/notifications?unread=true, kept live
//    by `lexistar:notification` on the /tracking socket (the server puts every
//    session socket in its own `user:{id}` room; `lexistar:join-user` asks
//    again after a reconnect).
//  - The dropdown lists the latest notifications (severity chip, title, body,
//    age); opening one marks it read and follows its deep link; "Mark all
//    read" clears the count (POST /notifications:markRead {ids | all}).
//  - Critical notifications are announced to screen readers (aria-live).
//  - Dormant: rendered only for users with a LexiStar permission, and hidden
//    when the API answers 404/503 (LexiStar not enabled on this server).
//
// ST-00251: "See all notifications" opens the inbox page (/lexistar/notifications). The socket,
// the unread total and "marked read" are shared with that page and the sidebar's badge
// (lexistarNotificationStream): one live connection per tab, one count everywhere.
//
// Mounted once in the app shell (App.js ShellFrame), outside every router, so
// links go through AppLink (works tabbed and untabbed).
//
// ST-00280: titles and texts are shown with their event codes in words (an older notification
// still says TRIP.ARRIVED_SOURCE in its title; utils/eventTypeLabels withEventLabels).

import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type ComponentType,
  type Context,
  type CSSProperties,
} from 'react';
import { FaBell } from 'react-icons/fa';
import AppLinkJs from './AppLink';
import { useAuth } from '../../context/AuthContext';
import TabContextJs from '../../context/TabContext';
import { formatRelativeTime } from '../../utils/dateUtils';
import { withEventLabels } from '../../utils/eventTypeLabels';
import { listInbox, markInboxRead } from '../../services/lexistarInbox';
import type { InboxNotification } from '../../types/lexistar-notification-inbox';
import {
  NOTIFICATION_EVENT as SHARED_NOTIFICATION_EVENT,
  publishMarked,
  subscribeMarked,
  subscribeNotifications,
  unreadStore,
  useLexiStarUnread,
  type LiveNotification,
} from './lexistarNotificationStream';

export const NOTIFICATION_EVENT = SHARED_NOTIFICATION_EVENT;
/** The inbox page (ST-00251). */
export const INBOX_PATH = '/lexistar/notifications';
const LIST_LIMIT = 20;
const SOURCE = 'bell';

const AppLink = AppLinkJs as unknown as ComponentType<
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string }
>;
/** TabContext.js is createContext(null): typed with the field the bell reads. */
const TabContext = TabContextJs as unknown as Context<{ tabs?: unknown[] } | null>;

const SEVERITY: Record<string, { label: string; className: string }> = {
  C: { label: 'Critical', className: 'bg-red-600 text-red-50' },
  H: { label: 'High', className: 'bg-orange-100 text-orange-800' },
  M: { label: 'Medium', className: 'bg-amber-100 text-amber-800' },
  L: { label: 'Low', className: 'bg-blue-50 text-blue-700' },
  I: { label: 'Info', className: 'bg-slate-100 text-slate-600' },
};
export const severityMetaOf = (s: string | null | undefined) =>
  SEVERITY[s ?? ''] ?? (SEVERITY.I as { label: string; className: string });

type BellItem = InboxNotification | (LiveNotification & { created_at?: string });

/** Absolute same-origin links → path; anything else stays as it is. */
export function linkPath(link: string | null | undefined): string | null {
  if (!link) return null;
  try {
    const u = new URL(link, window.location.origin);
    if (u.origin === window.location.origin) return `${u.pathname}${u.search}${u.hash}`;
    return null;
  } catch {
    return String(link).startsWith('/') ? link : null;
  }
}

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

export default function LexiStarNotificationBell() {
  const { hasPermission, authenticated, authEnabled } = useAuth();
  const eligible =
    (!authEnabled || authenticated) &&
    (hasPermission('lexistar.trips.view') || hasPermission('lexistar.notifications.view'));

  const [available, setAvailable] = useState(true);
  const unread = useLexiStarUnread();
  const [items, setItems] = useState<BellItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [announce, setAnnounce] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Desktop: sit in the right end of the back/forward bar, below the tab strip
  // when tabs are on (28 px with one tab, 34 px with several).
  const tabs = useContext(TabContext);
  const desktopTop = tabs ? ((tabs.tabs?.length || 0) > 1 ? 34 : 28) + 2 : 2;

  const refreshCount = useCallback(async () => {
    try {
      const res = (await listInbox({ unread: true, limit: LIST_LIMIT })) as unknown as Record<string, unknown>;
      const rows = Array.isArray(res?.notifications) ? (res.notifications as unknown[]) : [];
      unreadStore.set(Number(res?.unread ?? res?.unread_count ?? rows.length) || 0);
      setAvailable(true);
    } catch (err) {
      const s = statusOf(err);
      if (s === 404 || s === 503 || s === 403) setAvailable(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = (await listInbox({ limit: LIST_LIMIT })) as unknown as Record<string, unknown>;
      setItems(Array.isArray(res?.notifications) ? (res.notifications as BellItem[]) : []);
      if (res?.unread !== undefined || res?.unread_count !== undefined) {
        unreadStore.set(Number(res.unread ?? res.unread_count) || 0);
      }
    } catch {
      /* the count stays; the list shows what it had */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!eligible) return undefined;
    refreshCount();
    const id = setInterval(refreshCount, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [eligible, refreshCount]);

  // Live: the user room on /tracking (the shared socket).
  useEffect(() => {
    if (!eligible || !available) return undefined;
    return subscribeNotifications((n) => {
      setItems((prev) =>
        prev.some((x) => x.id === n.id) ? prev : [n as BellItem, ...prev].slice(0, LIST_LIMIT)
      );
      if (!n.read_at) unreadStore.add(1);
      if (n.severity === 'C') setAnnounce(`Critical: ${withEventLabels(n.title)}`);
    });
  }, [eligible, available]);

  // The inbox page marked some read: show them read here too.
  useEffect(
    () =>
      subscribeMarked((e, source) => {
        if (source === SOURCE) return;
        const now = new Date().toISOString();
        setItems((prev) =>
          prev.map((x) =>
            'all' in e || e.ids.includes(x.id) ? { ...x, read_at: x.read_at || now } : x
          )
        );
      }),
    []
  );

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!eligible || !available) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markRead = async (ids: string[] | 'all') => {
    const now = new Date().toISOString();
    setItems((prev) =>
      prev.map((x) =>
        ids === 'all' || ids.includes(x.id) ? { ...x, read_at: x.read_at || now } : x
      )
    );
    if (ids === 'all') unreadStore.set(0);
    else unreadStore.add(-ids.length);
    publishMarked(ids === 'all' ? { all: true } : { ids }, SOURCE);
    try {
      await markInboxRead(ids === 'all' ? { all: true } : { ids });
    } catch {
      refreshCount();
    }
  };

  const onItem = (n: BellItem) => {
    if (!n.read_at) markRead([n.id]);
    setOpen(false);
  };

  // z-40 = the mobile header's layer: the bell comes after the header in the
  // DOM (so it shows on it) and before the page, whose drawers (z-40) and
  // dialogs (z-50) therefore cover it.
  return (
    <div
      ref={rootRef}
      className="fixed z-40 right-3 top-2.5 md:top-[var(--lx-bell-top)] md:right-4"
      style={{ '--lx-bell-top': `${desktopTop}px` } as CSSProperties}
      data-testid="lexistar-bell"
    >
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative inline-flex items-center justify-center w-9 h-9 md:w-8 md:h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <FaBell size={15} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-600 text-[10px] font-bold leading-[1.1rem] text-red-50 text-center tabular-nums">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <span className="sr-only" aria-live="assertive">
        {announce}
      </span>
      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 mt-1 w-[min(24rem,calc(100vw-1.5rem))] max-h-[70vh] flex flex-col bg-surface rounded-xl shadow-2xl ring-1 ring-slate-200 overflow-hidden"
        >
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-900">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markRead('all')}
                className="text-xs font-medium text-primary-700 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <ul
            className="overflow-y-auto divide-y divide-slate-100"
            aria-label="Recent notifications"
          >
            {loading && items.length === 0 && (
              <li className="px-4 py-6 text-sm text-slate-500">Loading…</li>
            )}
            {!loading && items.length === 0 && (
              <li className="px-4 py-6 text-sm text-slate-500">
                Nothing yet. Alerts from your LexiStar rules appear here.
              </li>
            )}
            {items.map((n) => {
              const sev = severityMetaOf(n.severity);
              const path = linkPath(n.link);
              const content = (
                <span className="flex gap-3">
                  <span
                    className={`mt-1.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${n.read_at ? 'bg-transparent' : 'bg-primary-500'}`}
                    aria-label={n.read_at ? undefined : 'Unread'}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span
                        className={`px-1.5 py-px rounded text-[10px] font-bold uppercase tracking-wide ${sev.className}`}
                      >
                        {sev.label}
                      </span>
                      <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">
                        {formatRelativeTime(n.created_at)}
                      </span>
                    </span>
                    <span
                      className={`block mt-0.5 text-sm ${n.read_at ? 'text-slate-700' : 'font-semibold text-slate-900'}`}
                    >
                      {withEventLabels(n.title)}
                    </span>
                    {n.body && (
                      <span className="block text-xs text-slate-600 line-clamp-2">
                        {withEventLabels(n.body)}
                      </span>
                    )}
                  </span>
                </span>
              );
              return (
                <li key={n.id}>
                  {path ? (
                    <AppLink
                      to={path}
                      onClick={() => onItem(n)}
                      className="block px-4 py-3 hover:bg-slate-50"
                    >
                      {content}
                    </AppLink>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onItem(n)}
                      className="block w-full text-left px-4 py-3 hover:bg-slate-50"
                    >
                      {content}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gap-3">
            <AppLink
              to={INBOX_PATH}
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-primary-700 hover:underline"
            >
              See all notifications
            </AppLink>
            {hasPermission('lexistar.messages.view') && (
              <AppLink
                to="/lexistar/messages"
                onClick={() => setOpen(false)}
                className="text-xs font-medium text-primary-700 hover:underline"
              >
                Message log
              </AppLink>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
